// Accueil : qui est là aujourd'hui, où en sont les tâches, missions, BVP, planning chargé.
// L'ancien accueil RH (DashboardRH) s'affiche en dessous seulement si le module RH est actif.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { format, startOfWeek, addDays } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Sun, Moon, CheckSquare, Target, Scale, FileSpreadsheet, AlertTriangle, ArrowRight } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { useModules } from '../lib/modules'
import DashboardRH from './DashboardRH'
import { useExcelOrder, sortByExcelOrder } from '../lib/excelOrder'

interface Presence { heureDebut?: string; heureFin?: string; creneaux?: { start: string; end: string }[] }
interface Emp { id: string; firstName: string; lastName: string; present: boolean; presence?: Presence }
interface DayTask { id: string; nom: string; zone1?: string; validated: boolean; assignedTo: { id: string; name: string }[] }
interface Mission { id: string; title: string; priority: string; periodEnd?: string | null; assignedToNames: string[] }
interface Bvp { id: string; date: string; heure: string; produit: string; resultat: string; employeeName: string }
interface LastImport { fileName: string; createdAt: string; dateFrom: string; dateTo: string; shiftsCreated: number }

const hhmm = (t?: string) => (t || '').slice(0, 5)
const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m }

export default function Dashboard() {
  const { current: establishment } = useEstablishment()
  const { modules } = useModules()
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const [employees, setEmployees] = useState<Emp[]>([])
  const [tasks, setTasks] = useState<DayTask[]>([])
  const [missions, setMissions] = useState<Mission[]>([])
  const [bvp, setBvp] = useState<Bvp[]>([])
  const [lastImport, setLastImport] = useState<LastImport | null>(null)
  const [nextWeekShifts, setNextWeekShifts] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const excelOrder = useExcelOrder(establishment?.id)

  const today = new Date()
  const nextMonday = addDays(startOfWeek(today, { weekStartsOn: 1 }), 7)

  useEffect(() => {
    if (!establishment) return
    const id = establishment.id
    setLoading(true)
    // Chaque bloc se charge indépendamment : une erreur n'empêche pas les autres
    Promise.allSettled([
      api.get(`/tasks/today?establishmentId=${id}&light=1`).then(r => { setEmployees(r.data.employees || []); setTasks(r.data.tasks || []) }),
      api.get(`/missions?establishmentId=${id}&status=todo`).then(r => setMissions(r.data || [])),
      api.get(`/bvp/controls?establishmentId=${id}&month=${format(today, 'yyyy_MM')}`).then(r => setBvp((r.data || []).slice(0, 5))),
      api.get(`/planning-import/last?establishmentId=${id}`).then(r => setLastImport(r.data)),
      api.get(`/shifts?weekStart=${format(nextMonday, 'yyyy-MM-dd')}&establishmentId=${id}`).then(r => setNextWeekShifts((r.data || []).length)),
    ]).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishment])

  // Présents dans l'ordre des lignes du planning Excel
  const present = sortByExcelOrder(employees.filter(e => e.present), excelOrder)
  const slots = (e: Emp) => e.presence?.creneaux?.length
    ? e.presence.creneaux
    : e.presence?.heureDebut ? [{ start: e.presence.heureDebut, end: e.presence.heureFin || '' }] : []
  // Affichage : un horaire qui commence avant midi est « matin », sinon « après-midi » ;
  // une longue journée (fin après 16h) compte aussi l'après-midi
  const matin = present.filter(e => slots(e).some(s => toMin(s.start) < 12 * 60))
  const aprem = present.filter(e => slots(e).some(s => toMin(s.start) >= 12 * 60 || (!!s.end && toMin(s.end) >= 16 * 60)))
  const done = tasks.filter(t => t.validated).length
  const unassigned = tasks.filter(t => !t.validated && t.assignedTo.length === 0).length
  const percent = tasks.length ? Math.round((done / tasks.length) * 100) : 0
  const lateMissions = missions.filter(m => m.periodEnd && m.periodEnd < format(today, 'yyyy-MM-dd'))
  const nonConformes = bvp.filter(b => b.resultat === 'NON_CONFORME').length

  const PresenceList = ({ list, icon, title }: { list: Emp[]; icon: JSX.Element; title: string }) => (
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">{icon}{title} ({list.length})</p>
      {list.length === 0 ? <p className="text-sm text-gray-400">Personne</p> : (
        <ul className="space-y-1">
          {list.map(e => (
            <li key={e.id} className="flex items-center justify-between text-sm">
              <span className="text-gray-800 truncate">{e.firstName} {e.lastName?.charAt(0) ? e.lastName.charAt(0) + '.' : ''}</span>
              <span className="text-gray-400 tabular-nums text-xs">{slots(e).map(s => `${hhmm(s.start)}-${hhmm(s.end)}`).join(' / ')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bonjour {user.firstName}</h1>
        <p className="text-gray-500 mt-1 capitalize">{format(today, 'EEEE d MMMM yyyy', { locale: fr })} · {establishment?.name}</p>
      </div>

      {/* Alerte planning */}
      {!loading && nextWeekShifts === 0 && (
        <Link to="/planning" className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 hover:bg-amber-100">
          <AlertTriangle size={18} />
          Aucun horaire pour la semaine du {format(nextMonday, 'd MMMM', { locale: fr })} : pensez à charger le planning Excel.
          <ArrowRight size={14} className="ml-auto" />
        </Link>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Présents */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Présents aujourd'hui</h2>
            <span className="text-sm text-gray-500">{present.length} personne{present.length > 1 ? 's' : ''}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-6">
            <PresenceList list={matin} icon={<Sun size={13} className="text-amber-500" />} title="Matin" />
            <PresenceList list={aprem} icon={<Moon size={13} className="text-indigo-500" />} title="Après-midi" />
          </div>
        </div>

        {/* Tâches du jour */}
        <Link to="/tasks" className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4"><CheckSquare size={18} className="text-teal-600" /> Tâches du jour</h2>
          <p className="text-3xl font-bold text-gray-900">{done}<span className="text-lg text-gray-400"> / {tasks.length}</span></p>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mt-3">
            <div className={`h-full ${percent >= 80 ? 'bg-green-500' : percent >= 40 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${percent}%` }} />
          </div>
          <p className="text-sm text-gray-500 mt-2">{tasks.length - done} à faire</p>
          {unassigned > 0 && <p className="text-sm text-red-600 mt-1">{unassigned} sans personne (aucun présent compatible)</p>}
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Missions */}
        <Link to="/missions" className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Target size={18} className="text-purple-600" /> Missions en cours ({missions.length})</h2>
          {lateMissions.length > 0 && <p className="text-sm text-red-600 mb-2">{lateMissions.length} en retard</p>}
          <ul className="space-y-1.5">
            {missions.slice(0, 5).map(m => (
              <li key={m.id} className="text-sm flex items-center justify-between gap-2">
                <span className="truncate text-gray-800">{m.priority === 'urgent' || m.priority === 'high' ? '🔴 ' : ''}{m.title}</span>
                {m.periodEnd && <span className="text-xs text-gray-400 flex-shrink-0">{format(new Date(m.periodEnd + 'T12:00:00'), 'dd/MM')}</span>}
              </li>
            ))}
            {missions.length === 0 && <li className="text-sm text-gray-400">Aucune mission en cours</li>}
          </ul>
        </Link>

        {/* BVP */}
        <Link to="/bvp" className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Scale size={18} className="text-yellow-600" /> Derniers contrôles BVP</h2>
          {nonConformes > 0 && <p className="text-sm text-red-600 mb-2">{nonConformes} non conforme{nonConformes > 1 ? 's' : ''}</p>}
          <ul className="space-y-1.5">
            {bvp.map(b => (
              <li key={b.id} className="text-sm flex items-center justify-between gap-2">
                <span className="truncate text-gray-800">{b.resultat === 'CONFORME' ? '✅' : '❌'} {b.produit}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">{b.date.slice(0, 5)} {b.heure}</span>
              </li>
            ))}
            {bvp.length === 0 && <li className="text-sm text-gray-400">Aucun contrôle ce mois-ci</li>}
          </ul>
        </Link>

        {/* Planning chargé */}
        <Link to="/planning" className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><FileSpreadsheet size={18} className="text-green-600" /> Planning Excel</h2>
          {lastImport ? (
            <div className="text-sm text-gray-600 space-y-1">
              <p>Chargé le {format(new Date(lastImport.createdAt), 'd MMMM à HH:mm', { locale: fr })}</p>
              <p>Couvre du {format(new Date(lastImport.dateFrom + 'T12:00:00'), 'd MMM', { locale: fr })} au {format(new Date(lastImport.dateTo + 'T12:00:00'), 'd MMM', { locale: fr })}</p>
              <p className="text-xs text-gray-400 truncate">{lastImport.fileName} · {lastImport.shiftsCreated} horaires</p>
            </div>
          ) : <p className="text-sm text-gray-400">Aucun planning Excel chargé pour l'instant</p>}
        </Link>
      </div>

      {modules.rh && (
        <div className="border-t border-gray-200 pt-2 -mx-6 md:-mx-8">
          <DashboardRH />
        </div>
      )}
    </div>
  )
}
